/**
 * WHAT A PERSON MAY DO TO A POLICY, DECIDED ONCE.
 *
 * Pure, table-tested, and mirrored verbatim from
 * `functions/src/policies/policyCapabilities.ts`. A guard test reads both
 * files and fails when they drift. The shape follows
 * `governance/stepEligibility.ts`, which is the same idea for approval steps:
 * the screen and the gate ask one question and get one answer, so a button is
 * never offered for a call that will be refused, and never hidden from
 * somebody the server would have allowed.
 *
 * Both failure directions were real. The record page tested ownership with
 * `ownerId` alone, so a person who CREATED a policy but is not its named owner
 * was shown nothing; and it tested ownership with no role floor at all, so a
 * viewer who happened to be the owner was shown buttons every callable
 * refuses. The document panel had no ownership test whatever, so any author in
 * the organization was invited to replace the document on a colleague's draft
 * and found out after the browser had already uploaded the file.
 *
 * ONE THING THIS DELIBERATELY DOES NOT DO: unify the owner field sets. Six
 * callables disagree about what "owner" means — `savePolicyDraft` counts
 * co-owners and `deletePolicyDraft` does not, `startPolicyRevision` counts
 * `ownerUserId` and `discardPolicyRevision` counts `revisionStartedBy`
 * instead. Each capability below reproduces its own callable's rule exactly,
 * because making them agree would grant somebody something they cannot do
 * today, and that is a decision about authority rather than about screens.
 * The disagreement is recorded here so it can be settled deliberately.
 */

/** The role names, ranked. Mirrors `utils/roles.ts`. */
export const ROLE_RANK: Record<string, number> = {
  viewer: 1, author: 2, reviewer: 3, approver: 4, admin: 5, sysadmin: 6,
};

/** Hierarchical, as `assertRole` is. */
export function atLeast(roles: readonly string[], required: string): boolean {
  const need = ROLE_RANK[required] ?? 99;
  return roles.some((r) => (ROLE_RANK[r] ?? 0) >= need);
}

/** Exact membership, as `assertAnyRole` is. */
export function anyOf(roles: readonly string[], allowed: readonly string[]): boolean {
  return roles.some((r) => allowed.includes(r));
}

/** The five roles that may touch a draft at all. */
export const DRAFT_ROLES = ['author', 'reviewer', 'approver', 'admin', 'sysadmin'] as const;
/** Who may set a review interval. `reviewer` is absent, as the callable has it. */
export const INTERVAL_ROLES = ['author', 'approver', 'admin', 'sysadmin'] as const;

/** Everything a decision needs from the policy record. */
export interface PolicyFacts {
  status?: string | null;
  revisionStatus?: string | null;
  /** Imported or externally managed: several callables refuse it outright. */
  readOnly?: boolean | null;
  source?: string | null;
  ownerId?: string | null;
  createdBy?: string | null;
  ownerUserId?: string | null;
  revisionStartedBy?: string | null;
  coOwnerIds?: readonly string[] | null;
  /** Who submitted the round that is open. Mirrors the instance's `createdBy`. */
  submittedBy?: string | null;
}

export interface Actor {
  uid: string | null;
  roles: readonly string[];
}

const is = (v: string | null | undefined, uid: string | null) => Boolean(uid) && v === uid;
const inList = (l: readonly string[] | null | undefined, uid: string | null) =>
  Boolean(uid) && Array.isArray(l) && l.includes(uid!);

/** `ownerId | createdBy | coOwnerIds` — savePolicyDraft, markReviewedNoChanges. */
export const ownerWithCoOwners = (p: PolicyFacts, uid: string | null) =>
  is(p.ownerId, uid) || is(p.createdBy, uid) || inList(p.coOwnerIds, uid);
/** `ownerId | createdBy` — deletePolicyDraft, setReviewInterval. */
export const ownerPlain = (p: PolicyFacts, uid: string | null) =>
  is(p.ownerId, uid) || is(p.createdBy, uid);
/** `ownerId | createdBy | revisionStartedBy` — submit, discardRevision, attachments. */
export const ownerWithReviser = (p: PolicyFacts, uid: string | null) =>
  is(p.ownerId, uid) || is(p.createdBy, uid) || is(p.revisionStartedBy, uid);
/** `ownerId | createdBy | ownerUserId` — startPolicyRevision. */
export const ownerWithUserId = (p: PolicyFacts, uid: string | null) =>
  is(p.ownerId, uid) || is(p.createdBy, uid) || is(p.ownerUserId, uid);
/** `ownerId | createdBy | ownerUserId | coOwnerIds` — updatePolicyMetadata. */
export const ownerWidest = (p: PolicyFacts, uid: string | null) =>
  ownerWithUserId(p, uid) || inList(p.coOwnerIds, uid);

/** Read-only content, which five callables refuse before anything else. */
export const isReadOnly = (p: PolicyFacts) => Boolean(p.readOnly) || Boolean(p.source);

const published = (p: PolicyFacts) => p.status === 'published';
/** A draft, or the draft of a next version: the states a document can be worked on in. */
export const isEditableDraft = (p: PolicyFacts) =>
  p.status === 'draft' || (published(p) && p.revisionStatus === 'draft');
/** In a round of approval, either as a draft or as a revision. */
export const isInApproval = (p: PolicyFacts) =>
  p.status === 'in_review' || (published(p) && p.revisionStatus === 'in_review');
/** Every step satisfied, waiting only for its effective date. */
export const isApprovedAndHeld = (p: PolicyFacts) =>
  p.status === 'approved' || (published(p) && p.revisionStatus === 'approved');

export interface PolicyCapabilities {
  editDraft: boolean;
  editDetails: boolean;
  replaceDocument: boolean;
  convertToPdf: boolean;
  submit: boolean;
  withdraw: boolean;
  publishNow: boolean;
  startRevision: boolean;
  discardRevision: boolean;
  markReviewed: boolean;
  setReviewInterval: boolean;
  retire: boolean;
  deleteDraft: boolean;
}

/**
 * Every answer, from one read of the record. Each line names the callable it
 * reproduces so the two can be checked against each other by eye.
 */
export function policyCapabilities(p: PolicyFacts, actor: Actor): PolicyCapabilities {
  const { uid, roles } = actor;
  const admin = atLeast(roles, 'admin');
  const draftRole = anyOf(roles, DRAFT_ROLES) || admin;
  const locked = isReadOnly(p);
  const retired = p.status === 'retired';

  return {
    // savePolicyDraft: five roles, not read-only, status draft, owner+coOwners or admin.
    editDraft: draftRole && !locked && p.status === 'draft' && (ownerWithCoOwners(p, uid) || admin),

    // updatePolicyMetadata: five roles, not read-only, not retired; a draft is
    // the owner's or an administrator's, anything else is an administrator's.
    editDetails: draftRole && !locked && !retired
      && (p.status === 'draft' ? (ownerWidest(p, uid) || admin) : admin),

    // setDraftDocument / removeDraftAttachment: five roles, an open draft, owner+reviser or admin.
    replaceDocument: draftRole && isEditableDraft(p) && (ownerWithReviser(p, uid) || admin),
    convertToPdf: draftRole && isEditableDraft(p) && (ownerWithReviser(p, uid) || admin),

    // submitPolicyForReview: five roles, not read-only, an open draft, owner+reviser or admin.
    submit: draftRole && !locked && isEditableDraft(p) && (ownerWithReviser(p, uid) || admin),

    // withdrawPolicyFromApproval: author and above, in approval, and the
    // person who submitted it, its owner, a co-owner, or an administrator.
    withdraw: atLeast(roles, 'author') && isInApproval(p)
      && (is(p.submittedBy, uid) || ownerWithCoOwners(p, uid) || admin),

    // publishPolicy: approver and above, and a finished approval. No ownership.
    publishNow: atLeast(roles, 'approver') && isApprovedAndHeld(p),

    // startPolicyRevision: five roles, not read-only, published, no revision open, owner+userId or admin.
    startRevision: draftRole && !locked && published(p) && !p.revisionStatus
      && (ownerWithUserId(p, uid) || admin),

    // discardPolicyRevision: five roles, published with a revision still a draft, owner+reviser or admin.
    discardRevision: draftRole && published(p) && p.revisionStatus === 'draft'
      && (ownerWithReviser(p, uid) || admin),

    // markReviewedNoChanges: five roles, published, owner+coOwners or admin.
    // NOTE: the callable does not care whether a revision is open, so neither does this.
    markReviewed: draftRole && published(p) && (ownerWithCoOwners(p, uid) || admin),

    // setReviewInterval: four roles (no reviewer), owner or admin. No status rule.
    setReviewInterval: (anyOf(roles, INTERVAL_ROLES) || admin) && (ownerPlain(p, uid) || admin),

    // retirePolicy: administrator, not read-only, published.
    retire: admin && !locked && published(p),

    // deletePolicyDraft: five roles, status draft, owner or admin.
    deleteDraft: draftRole && p.status === 'draft' && (ownerPlain(p, uid) || admin),
  };
}
